import React from 'react'
import styled from 'styled-components'
import { parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import { useTheme } from '@mui/material/styles'
import Grid from '@mui/material/Grid'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import Typography from '@/v2/components/common/Typography'
import IconButton from '@/v2/components/common/IconButton'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import IconMenu from '@/v2/components/common/IconMenu'
import useToggle from '@/v2/hooks/useToggle'
import { DAY_IN_US_FORMAT } from '@/v2/constants'
import { currency } from '@/v2/utils/format'
import InvoiceLabel from '../shared/InvoiceLabel'
import Invoice from '../Invoice'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import useModals from '@/v2/hooks/useModals'

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.white.main};
`
const Head = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`
const Body = styled.div`
  background: ${({ theme }) => theme.palette.white.main};
  display: flex;
  flex-direction: column;
  flex: 1;
`
const HeadContent = styled(Box)`
  padding: 26px 34px;
  display: flex;
  width: 100%;

  @media (max-width: 768px) {
    padding: 16px;
    flex-direction: column;
  }
`
const MobileRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 12px;
  }
`
const MobileItem = styled.div`
  flex: 1;
  min-width: 45%;
`
const DesktopGrid = styled(Grid)`
  @media (max-width: 768px) {
    display: none !important;
  }
`
const PlanHeader = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const InstallmentsPayment = ({ plan, paymentMethods, patientId }) => {
  const theme = useTheme()
  const { open, handleToggle } = useToggle()
  const { goTo } = useModals()

  if (plan.invoices.length === 1) {
    const invoice = plan.invoices[0]
    return (
      <Invoice
        key={`invoice-${invoice.id}`}
        plan={plan}
        invoice={invoice}
        paymentMethods={paymentMethods}
        isSingle
      />
    )
  }

  const hasDownPayment = parseFloat(plan.contract?.downPayment || 0) > 0
  const isSinglePayment = !hasDownPayment && plan.installments === 1
  const installmentsDisplay = plan.installments
  const installmentsPaidDisplay = `${plan.installmentsProcessed}/${plan.installments}`

  return (
    <Wrapper theme={theme}>
      <Head open={open}>
        <HeadContent>
          <DesktopGrid container spacing={1} columns={9}>
            <Grid item xs={2}>
              <Box display={'flex'} gap={1} flexDirection={'row'}>
                <Typography wordBreak bold lineHeight="23px">
                  {`${plan.description}`}
                </Typography>

                {!isSinglePayment && (
                  <Typography
                    fontSize="10px"
                    lineHeight="23px"
                    color={theme.palette.gray.dark}
                  >
                    {`(${installmentsDisplay} ${
                      installmentsDisplay > 1 ? 'MONTHS' : 'MONTH'
                    })`}
                  </Typography>
                )}
              </Box>

              {plan.contract && (
                <Typography
                  fontSize="12px"
                  color={theme.palette.gray.dark}
                  mb={0.5}
                >
                  Contract #
                  {plan.contract.identifierToken ||
                    plan.contract.patientLegalName}
                </Typography>
              )}
              <InvoiceLabel status={plan.status} />
            </Grid>
            <Grid item xs={1}>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                START DATE
              </Typography>
              <Typography fontSize="15px">
                {safeFormat(parseISO(plan.startedAt), DAY_IN_US_FORMAT)}
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                TOTAL
              </Typography>
              <Typography fontSize="15px">
                {currency(Number(plan.finalAmount).toFixed(2))}
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                PAID
              </Typography>
              <Typography fontSize="15px">
                {currency(Number(plan.paid).toFixed(2))}
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                PROCESSING FEES
              </Typography>
              <Typography fontSize="15px">
                {currency(Number(plan.processingFeesPaid || 0).toFixed(2))}
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                REMAINING
              </Typography>
              <Typography fontSize="15px">
                {currency(Number(plan.remaining).toFixed(2))}
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                REFUNDED
              </Typography>
              <Typography fontSize="15px">
                {currency(Number(plan.refunded).toFixed(2))}
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                INSTALLMENTS PAID
              </Typography>
              <Typography fontSize="15px">{installmentsPaidDisplay}</Typography>
            </Grid>
          </DesktopGrid>

          {/* Mobile Layout */}
          <PlanHeader>
            <Box
              display={'flex'}
              gap={1}
              flexDirection={'row'}
              alignItems={'center'}
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <Typography wordBreak bold lineHeight="23px">
                {`${plan.description}`}
              </Typography>
              {!isSinglePayment && (
                <Typography
                  fontSize="10px"
                  lineHeight="23px"
                  color={theme.palette.gray.dark}
                >
                  {`(${installmentsDisplay} ${
                    installmentsDisplay > 1 ? 'MONTHS' : 'MONTH'
                  })`}
                </Typography>
              )}
              {!isSinglePayment && (
                <IconMenu
                  icon={<MoreVertIcon fontSize="small" />}
                  aria-label="More"
                  title="More"
                  style={{ marginLeft: 'auto' }}
                  options={[
                    {
                      label: 'Initial Payment',
                      onClick: () =>
                        goTo(
                          `/v2/patient/${patientId}/finance/advanced-payment`,
                          {
                            plan: plan,
                          }
                        ),
                    },
                  ]}
                />
              )}
            </Box>
            {plan.contract && (
              <Typography
                fontSize="12px"
                color={theme.palette.gray.dark}
                mb={0.5}
                sx={{ display: { xs: 'block', md: 'none' } }}
              >
                Contract #
                {plan.contract.identifierToken ||
                  plan.contract.patientLegalName}
              </Typography>
            )}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <InvoiceLabel status={plan.status} />
            </Box>
          </PlanHeader>

          <MobileRow>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                START DATE
              </Typography>
              <Typography fontSize="14px">
                {safeFormat(parseISO(plan.startedAt), DAY_IN_US_FORMAT)}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                TOTAL
              </Typography>
              <Typography fontSize="14px">
                {currency(Number(plan.finalAmount).toFixed(2))}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                PAID
              </Typography>
              <Typography fontSize="14px">
                {currency(Number(plan.paid).toFixed(2))}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                PROCESSING FEES
              </Typography>
              <Typography fontSize="14px">
                {currency(Number(plan.processingFeesPaid || 0).toFixed(2))}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                REMAINING
              </Typography>
              <Typography fontSize="14px">
                {currency(Number(plan.remaining).toFixed(2))}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                REFUNDED
              </Typography>
              <Typography fontSize="14px">
                {currency(Number(plan.refunded).toFixed(2))}
              </Typography>
            </MobileItem>
            <MobileItem>
              <Typography
                fontSize="10px"
                lineHeight="23px"
                color={theme.palette.gray.dark}
              >
                INSTALLMENTS PAID
              </Typography>
              <Typography fontSize="14px">{installmentsPaidDisplay}</Typography>
            </MobileItem>
          </MobileRow>

          {!isSinglePayment && (
            <IconMenu
              icon={<MoreVertIcon fontSize="small" />}
              aria-label="More"
              title="More"
              sx={{
                marginLeft: 'auto',
                marginRight: '0',
                marginTop: 'auto',
                display: { xs: 'none', md: 'block' },
              }}
              options={[
                {
                  label: 'Initial Payment',
                  onClick: () =>
                    goTo(`/v2/patient/${patientId}/finance/advanced-payment`, {
                      plan: plan,
                    }),
                },
              ]}
            />
          )}
        </HeadContent>
        <Grid
          container
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          flex={1}
          bgcolor={theme.palette.gray.default}
          sx={{
            cursor: 'pointer',
          }}
          onClick={() => handleToggle()}
        >
          <IconButton title="Show More" bordercolor="transparent">
            <>{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</>
          </IconButton>
        </Grid>
      </Head>

      {open && (
        <Body theme={theme}>
          {plan.invoices.map((invoice, key) => (
            <Invoice
              key={`invoice-${invoice.id}`}
              plan={plan}
              invoice={invoice}
              paymentMethods={paymentMethods}
              showBorder={key !== plan.invoices.length - 1}
            />
          ))}
        </Body>
      )}
    </Wrapper>
  )
}

export default InstallmentsPayment
